import { getUserIcon } from "@/framework/utilities/getUserIcon";
import { isEmpty } from "@/framework/utilities/isEmpty";
import { keysToCamelCase } from "@/framework/utilities/keysToCamelCase";
import { replaceKaigyo } from "@/framework/utilities/replaceKaigyo";
import { transformTextToLink } from "@/framework/utilities/transformTextToLink";
import store from "@/store";
import { i18n } from "@/framework/plugins/i18n";
import { Button } from "@/modules/staff/objects/conversation/Button";
import { Message } from "@/modules/staff/objects/conversation/Message";
import { MessageContent } from "@/modules/staff/objects/conversation/MessageContent";
import { UserProfile } from "@/modules/auth/objects/userProfile";
import { KtznProject, ProjectService } from "@/framework/services/general/projectService";
import { MediaService } from "@/framework/media/MediaService";
import { UserService } from "@/modules/auth/services/UserService";

const createMessageDate = (messages: Message[]): Date | null => {
  if (messages.length > 0) {
    const lastMsg: Message = messages[messages.length - 1];
    if (lastMsg.createdAt) {
      return new Date(Date.parse(lastMsg.createdAt));
    }
  }
  return null;
};

/** Goes through the "content" of a message, separating out the JSON into usable parts.
 * This also controls how and !WHAT! file types are put in parsed messages used in
 * Messages components, so any new types must be added here.
 * Current types are:
 * image (link to an image),
 * file (link to file, original name, size),
 * video (likely link and.. type? unused),
 * text (just the string text),
 * Anything added outside these types will be EXCLUDED from the final parsed message
 */
const parseMessages = async (rawMessages: Message[]): Promise<Message[]> => {
  const parsedMessages: Message[] = [];

  let messageDate: Date | null = createMessageDate(rawMessages);

  for (let rawMessage of rawMessages) {
    try {
      const parsedMessage = await parseMessage(rawMessage, messageDate);
      if (parsedMessage.content || parsedMessage.contents.length > 0) {
        parsedMessages.push(parsedMessage);
      }
    } catch (error) {
      // ignore invalid messages
    }
  }
  return parsedMessages;
};

const parseMessage = async (rawMessage: Message, messageDate: Date | null, source = ""): Promise<Message> => {
  let parsedMessage = rawMessage;
  // let messageDate = messageDateInput;
  // let currentUserName = currentUserNameInput;
  let currentLangCd = i18n.locale;

  const media = new MediaService();

  const userId = await new UserService().getUserAccessId();
  if (userId.isErr()) {
    throw new Error("User ID is not found");
  }

  if (rawMessage.author) rawMessage.author = parseAuthor(rawMessage.author);

  if (rawMessage.createdAt) {
    const nowYear = new Date().getFullYear();
    const msgDate: Date = new Date(Date.parse(rawMessage.createdAt));
    const y = msgDate.getFullYear();
    const m = msgDate.getMonth() + 1;
    const d = msgDate.getDate();
    const formatedDate = nowYear != y ? y + "/" + m + "/" + d : m + "/" + d;
    if (!messageDate) {
      messageDate = msgDate;
      parsedMessage.dateText = formatedDate;
    } else if (
      messageDate.getFullYear() !== msgDate.getFullYear() ||
      messageDate.getMonth() !== msgDate.getMonth() ||
      messageDate.getDate() !== msgDate.getDate()
    ) {
      parsedMessage.dateText = formatedDate;
    }
    messageDate = msgDate;
  }

  if (rawMessage.authorId === userId.value) {
    rawMessage.translating = false;
    rawMessage.translatedContent = replaceKaigyo(transformTextToLink(rawMessage.content));
    rawMessage.parsedTranslatedContents = [];
    if (rawMessage.translatedContents) {
      rawMessage.translatedContents.forEach(function (t) {
        const i = t.indexOf("/");
        const l = t.slice(0, i);
        const c = t.slice(i + 1);
        rawMessage.parsedTranslatedContents.push({
          langCd: l,
          content: replaceKaigyo(transformTextToLink(c)),
        });
      });
    }
  } else {
    //rawMessage.translating = rawMessage.translatedContents == null || rawMessage.translatedContents.length <= 1;
    let isExistingOriginalLang = false;
    rawMessage.parsedTranslatedContents = [];
    if (rawMessage.translatedContents) {
      rawMessage.translatedContents.forEach(function (t) {
        const i = t.indexOf("/");
        const l = t.slice(0, i);
        const c = t.slice(i + 1);
        rawMessage.parsedTranslatedContents.push({
          langCd: l,
          content: replaceKaigyo(transformTextToLink(c)),
        });
        if (currentLangCd === l) {
          rawMessage.translatedContent = replaceKaigyo(transformTextToLink(c));
          rawMessage.translating = false;
        }
        if (rawMessage.originalLangCd === l) {
          isExistingOriginalLang = true;
        }
      });

      if (!isExistingOriginalLang && !rawMessage.content.includes('{"messages":')) {
        // オリジナル言語のメッセージが翻訳になく、かつJSONではない場合のみ入れる
        rawMessage.parsedTranslatedContents.push({
          langCd: rawMessage.originalLangCd,
          content: replaceKaigyo(transformTextToLink(rawMessage.content)),
        });
      }
    }
    if (!rawMessage.translating) {
      if (rawMessage.translatedContent == null) {
        rawMessage.translatedContent = replaceKaigyo(transformTextToLink(rawMessage.content));
      }
    }
  }

  if (rawMessage.content && rawMessage.content.includes('{"messages":')) {
    const jsonResult = JSON.parse(rawMessage.content);
    const jsonArray = jsonResult.messages;

    // Set the content to empty so it doesn't display the JSON
    rawMessage.content = "";
    rawMessage.contents = [];
    let isLastFormOrButton = false;
    for (let jsonItem of jsonArray) {
      // @ts-ignore
      const msgContent: MessageContent = {};
      msgContent.style = jsonItem.style ? jsonItem.style : "bubble";
      if (jsonItem.type == "image") {
        msgContent.type = "image";
        if (jsonItem.style === "bubble") {
          msgContent.image = jsonItem.image.content;
          msgContent.style = jsonItem.image.style;
        } else {
          msgContent.image = media.resizeCloudinaryImage(jsonItem.image.content, 400) || jsonItem.image.content;
        }
      } else if (jsonItem.type == "file") {
        msgContent.type = "file";
        msgContent.file = jsonItem.file;
      } else if (jsonItem.type == "video") {
        msgContent.type = "video";
        msgContent.video = jsonItem.video;
      } else if (jsonItem.type == "text") {
        msgContent.type = "text";
        let textTmp = jsonItem.text.content;

        // 他の言語のメッセージも含まれている場合に、ユーザーの言語のものがあればそれを使う
        const userLangCd = store.getters.user.langCd || i18n.locale;
        let transTextList = jsonItem.text.translated_contents || [];
        for (const transText of transTextList) {
          const i = transText.indexOf("/");
          const l = transText.slice(0, i);
          const c = transText.slice(i + 1);
          if (userLangCd === l) {
            textTmp = c;
          }
        }

        if (textTmp) {
          textTmp = textTmp.replace(/\n/g, "<br>");
          let regexp = /\[(.+?)\|(https?:\/\/.+?)\]/g;
          const resultTmp = textTmp.match(regexp);
          if (resultTmp) {
            // ここにくるとすればアクティベーションの場合のみ、
            // そしてアクティベーションであれば直接リンクを入れたりはしていないはずなので、
            // [文字列|https://〜] と https://〜 が両方入ってるようなメッセージは来ないはず
            const linkPares = resultTmp ? resultTmp : [];
            for (let linkPare of linkPares) {
              regexp = /\[(.+?)\|(https?:\/\/.+?)\]/;
              const l = linkPare.match(regexp);
              if (!l) {
                continue;
              }
              const link_html = `<a href="${l[2]}" target="_blank">${l[1]}</a>`;
              textTmp = textTmp.replace(regexp, link_html);
            }
          }
          textTmp = transformTextToLink(textTmp);
        } else {
          textTmp = "";
        }
        msgContent.text = textTmp;
      } else if (jsonItem.type == "button") {
        msgContent.type = "button";
        // convert snake_case to camelCase
        const camelCaseItem: Button = keysToCamelCase(jsonItem.button);
        msgContent.button = camelCaseItem;
        isLastFormOrButton = true;
      } else if (jsonItem.type == "form") {
        msgContent.type = "form";
        msgContent.form = jsonItem.form;

        if (jsonItem.submit) {
          msgContent.submit = jsonItem.submit;
        }
        if (jsonItem.list) {
          msgContent.list = jsonItem.list;
        }
        isLastFormOrButton = true;
      } else if (jsonItem.type === "mention") {
        msgContent.type = "mention";
        msgContent.mention = jsonItem.mention.content;
      } else if (jsonItem.type === "sys_msg") {
        msgContent.type = "sys_msg";
        msgContent.sys_msg = {
          type: jsonItem.sys_msg.type,
          // 登録された言語か、英語か,日本語か、それもなければ諦める
          content:
            jsonItem.sys_msg.translatedContent[i18n.locale] ||
            jsonItem.sys_msg.translatedContent["en"] ||
            jsonItem.sys_msg.translatedContent["ja"] ||
            "",
          metaData: jsonItem.sys_msg.metaData,
        };
      } else if (jsonItem.type == "action") {
        msgContent.type = "action";
        msgContent.action = {
          type: jsonItem.action.type,
          id: "",
          password: "",
          target_conversation_id: "",
        };
        if (jsonItem.action.type === "onload") {
          msgContent.action.id = jsonItem.action.content.id;
          msgContent.action.password = jsonItem.action.content.password;
        } else if (jsonItem.action.type === "move_to_inquiry_page") {
          msgContent.action.target_conversation_id = jsonItem.action.target_conversation_id;
        }
      } else if (jsonItem.type === "quick_replies") {
        msgContent.type = "quick_replies";
        msgContent.buttons = jsonItem.buttons;
      } else if (jsonItem.type === "user_input") {
        msgContent.type = "user_input";
        msgContent.option = jsonItem;
        if (jsonItem.input_type === "table" || jsonItem.input_type === "tscan") {
          msgContent.type = "user_input_button";
          msgContent.button = {
            label: i18n.t("continue").toString(),
            payload: "open_hotel_menu",
            type: "open_hotel_menu",
            sns: "",
            transactionCode: "",
            color: "",
            url: "",
            serviceName: "",
            path: "",
          };
        }
      } else if (jsonItem.type === "survey") {
        msgContent.type = "survey";
        msgContent.survey = jsonItem.survey;
      }
      rawMessage.contents.push(msgContent);
    }
  } else {
    if (parsedMessage.content) parsedMessage.content.replace(/\n/g, "<br>");
  }

  // this is a terrible hack to prevent deleted messages from showing for users due to undeleted translations
  // should absolutely be removed once BE fixes it
  if (rawMessage.content == "deleted") {
    rawMessage.translatedContent = "deleted";
  }

  const project = new ProjectService().getCurrent();

  if (project === KtznProject.Staff) {
    if (source === "event") rawMessage.translatedContent = parseLamondoStaffMessages(rawMessage.lamondoSystemMessage);
    else rawMessage.translatedContent = parseLamondoStaffMessages(rawMessage.translatedContent);
  }

  setLlmVersion(rawMessage);

  return parsedMessage;
};

const setLlmVersion = (rawMessage: Message) => {
  if (isEmpty(rawMessage.contents)) return;

  const content = rawMessage.contents[0];
  if (isEmpty(content.mention)) return;

  const mention = content.mention[0];
  if (!isJsonString(mention)) return;

  const mentionJson = JSON.parse(mention);
  rawMessage.llmVersion = mentionJson.llmVersion;
};

// Function to check if a string is a valid JSON
const isJsonString = (str: string) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

const parseLamondoStaffMessages = (content: any) => {
  try {
    if (!isJsonString(content)) {
      return formatMessageContent(content);
    }

    const jsonObj = JSON.parse(content);
    const messageObj: { type: string; subType: string; message: string; translatedMessages: any } = {
      type: "",
      subType: "",
      message: "",
      translatedMessages: {},
    };

    if (!jsonObj.messages) {
      return content;
    }

    const sysMessage = findSysMessage(jsonObj.messages);
    if (!sysMessage) {
      return content;
    }

    messageObj.type = sysMessage.type;
    messageObj.subType = sysMessage.sys_msg.type;
    messageObj.message = formatMessageContent(sysMessage.sys_msg.content);

    const translatedContent = sysMessage.sys_msg.translatedContent;
    if (translatedContent) {
      messageObj.translatedMessages = formatTranslatedContent(translatedContent);
    }

    return messageObj;
  } catch (e) {
    return "error";
  }
};

const findSysMessage = (messages: any[]) => messages.find((message: any) => message.hasOwn("sys_msg"));

const formatMessageContent = (content: string) => replaceKaigyo(transformTextToLink(content.replace(/ \/ $/, "")));

const formatTranslatedContent = (translatedContent: any) => {
  const formattedContent: any = {};
  for (const [language, translatedText] of Object.entries(translatedContent)) {
    formattedContent[language] = formatMessageContent(translatedText as string);
  }
  return formattedContent;
};

const parseAuthor = (rawUser: UserProfile): UserProfile => {
  let author: UserProfile = {
    id: "",
    id_account: -1,
    username: "",
    email: "",
    userType: "",
    langCd: "",
    icon: "",
    qr: "",
    emailNotification: false,
    pushNotification: false,
    snsNotification: false,
    snsAccessId: "",
    snsType: "",
    activationType: "",
    signUpAt: "",
    isOfficial: false,
    sandboxType: "normal",
  };

  // TODO: Remove "ts-ignore" when the types are clear
  if (rawUser) {
    // @ts-ignore
    if (rawUser.access_id) {
      // @ts-ignore
      author.id = rawUser.access_id;
    }
    if (rawUser.id_account) {
      author.id_account = rawUser.id_account;
    }
    if (rawUser.username) {
      author.username = rawUser.username;
    }
    // @ts-ignore
    if (rawUser.user_type || rawUser.userType) {
      // @ts-ignore
      author.userType = rawUser.user_type || rawUser.userType;
    }
    // @ts-ignore
    if (rawUser.lang_cd || rawUser.langCd) {
      // @ts-ignore
      author.langCd = rawUser.lang_cd || rawUser.langCd;
    }
    author.icon = getUserIcon(rawUser);
    if (rawUser.qr) {
      author.qr = rawUser.qr;
    }
    // @ts-ignore
    if (rawUser.notification_email || rawUser.emailNotification) {
      author.emailNotification =
        // @ts-ignore
        rawUser.notification_email === 1 || rawUser.emailNotification === 1;
    }
    // @ts-ignore
    if (rawUser.notification_push || rawUser.pushNotification) {
      author.pushNotification =
        // @ts-ignore
        rawUser.notification_push === 1 || rawUser.pushNotification === 1;
    }
    // @ts-ignore
    if (rawUser.activation_type || rawUser.activationType) {
      // @ts-ignore
      author.activationType = rawUser.activation_type || rawUser.activationType;
    }
    // @ts-ignore
    if (rawUser.is_official || rawUser.isOfficial) {
      // @ts-ignore
      author.isOfficial = rawUser.is_official || rawUser.isOfficial;
    }
    // @ts-ignore
    if (rawUser.sandbox_type || rawUser.sandboxType) {
      // @ts-ignore
      author.sandboxType = rawUser.sandbox_type || rawUser.sandboxType;
    }
  }
  return author;
};

export default {
  parseMessages,
  parseMessage,
  createMessageDate,
};
